import { useReactiveVar } from '@apollo/client';

import { useCareerLessonDetailQuery } from 'core/graphql';
import { authState } from 'core/state';
import { UserRole } from 'context';

import { ICareerLesson, transformCareerLesson } from './transform';

interface IUseCareerLesson {
  lesson: ICareerLesson | null;
  loading: boolean;
  error: string;
  refetch: () => void;
}

/**
 * A single career lesson including its gated payload.
 *
 * The server withholds `content` / `videoUrl` / `attachments` for a locked
 * lesson, so the page renders the lock purely from `lesson.isUnlocked` — there
 * is no client-side unlock rule, date maths, or content guessing anywhere in
 * this feature (spec 0018 §3.2: eligibility is the single server-side source).
 *
 * `cache-and-network` so returning to a lesson after completing the previous
 * one shows the freshly-unlocked content rather than the cached lock.
 */
export const useCareerLesson = (lessonId: string): IUseCareerLesson => {
  const { active_role, isDemoUser } = useReactiveVar(authState);

  const { data, loading, error, refetch } = useCareerLessonDetailQuery({
    variables: { id: lessonId },
    fetchPolicy: 'cache-and-network',
    // Same non-Student skip as useCareerLessons (spec 0018 §3.3).
    skip: !lessonId || active_role !== UserRole.STUDENT || Boolean(isDemoUser),
  });

  return {
    lesson: transformCareerLesson(data),
    loading,
    error: error?.message || '',
    refetch,
  };
};
